/**
 * @description 现金返还
 */

import { RewardStatusEnum, RewardTypeEnum } from "@/types/reward";
import { RewardIcon } from "./reward-icon";
import { Button } from "@/components/ui/button";
import { useBalanceFormatter } from "@/hooks/use-balance-formatter";
import { useRewardCountDown } from "@/features/reward/hooks/useRewardCountDown";
import { useTranslations } from "next-intl";
import { truncateDecimals } from "@/libs/decimal";
import cn from "@/utils/classnames";
import { useClaimDebounce } from "./use-claim-debounce";

interface CashbackRewardProps {
  status: RewardStatusEnum;
  countdown?: number; // 秒
  next_distributed_at?: number; // 秒
  amount: number;
  currency: string;
  userLevel: number;
  lossAmount: number;
  claimCurrency: string;
  claimAmount: number;
  bonusWagerMultiple?: string;
  calendarAmount?: number;
  onClaim: () => Promise<unknown>;
  onTimeOut?: () => unknown;
}

export const CashbackReward = (props: CashbackRewardProps) => {
  const { status, next_distributed_at, amount, currency, lossAmount, onClaim, onTimeOut } = props;
  const { formatCurrencyDetail } = useBalanceFormatter();
  const { countdownStr, isOverOneDay } = useRewardCountDown({ targetDate: Number(next_distributed_at) * 1000, onEnd: onTimeOut });
  const t = useTranslations('reward');
  const noReward = status === RewardStatusEnum.NO_REWARD;
  const { loading, run } = useClaimDebounce(onClaim);

  if (![RewardStatusEnum.NO_REWARD, RewardStatusEnum.COUNT_DOWN, RewardStatusEnum.CLAIM].includes(status)) {
    return null;
  }

  const handleClaim = async () => {
    return run();
  };

  return (
    <div className="bg-dark-special rounded-lg py-3 pr-3 flex items-center justify-between"
      style={{
        background: 'linear-gradient(to bottom right, #1D3657 0%, #1B2131 65%, #1B2131 100%)',
      }}
    >
      <RewardIcon type={RewardTypeEnum.CASH_BACK} className="mr-2.5" />
      <div className="flex-1 text-xs font-medium leading-3 flex flex-col">
        <div className="text-white">{t('cashback')}</div>
        {noReward ? (
          <div className="text-tertiary mt-3 text-ss leading-4">
            {t('upToPercentLossRebate', { amount: `${truncateDecimals(lossAmount)}` })}
          </div>
        ) : (
          <>
            {!!amount && <div className="text-[#FFC600] text-xss font-medium leading-3 mt-3">{formatCurrencyDetail(amount)} {currency}</div>}
          </>
        )}
      </div>
      {!noReward && (
        <Button
          variant={status === RewardStatusEnum.CLAIM ? 'success' : 'cancel'}
          className={cn("h-8", status === RewardStatusEnum.COUNT_DOWN ? 'font-medium text-[13px] text-[#7A879A]  px-[10px] bg-[rgba(47,56,78,0.2)] disabled:opacity-1' : 'w-[90px]')}
          onClick={status === RewardStatusEnum.COUNT_DOWN ? undefined : handleClaim}
          disabled={status === RewardStatusEnum.COUNT_DOWN || loading}
          loading={loading && status === RewardStatusEnum.CLAIM}>
          {status === RewardStatusEnum.CLAIM && t('claim')}
          {status === RewardStatusEnum.COUNT_DOWN && <span className={`text-left ${isOverOneDay ? 'w-[71px]' : 'w-[70px] pl-[10px]'}`}>{countdownStr}</span>}
        </Button>
      )}
    </div>
  );
};
